import type { RicosTheme } from '@wix/ricos';
import { IChallengeStylesVars } from '../components/ChallengesPage/Settings.mobile/challengeStyles/challengeStyles.types';

const getParticularColorValue = (hostStyle, colorName) => {
  return hostStyle.siteColors.find(({ name }) => {
    return name === colorName;
  })?.value;
};

function isTransparent(colorString: string) {
  if (!colorString?.startsWith('rgba')) {
    return false;
  }

  // Remove "rgba(" and ")" and split by commas
  const rgbaValues = colorString
    .slice(5, -1)
    .split(',')
    .map((value) => value.trim());

  // Extract the alpha value (4th component)
  const alpha = parseFloat(rgbaValues[3]);

  return alpha === 0;
}

const getColorByKey = (
  hostStyle: any,
  colorKey: string,
  fallbackKey?: string,
) => {
  const textColorFromSettings =
    hostStyle?.styleParams?.colors?.[colorKey]?.value; // todo use it when the time will come

  const isTextColorFromSettingsTransparent = isTransparent(
    textColorFromSettings,
  );

  if (!textColorFromSettings || isTextColorFromSettingsTransparent) {
    return getParticularColorValue(hostStyle, fallbackKey || 'color_15');
  }

  return textColorFromSettings;
};
const calcCSSVarSyntax = ({
  cssVar,
  fallback,
  multiply,
}: {
  cssVar: string;
  multiply: number;
  fallback: number;
}) => {
  return `calc(var(${cssVar}, ${fallback}) * ${multiply}px)`;
};

const getCustomTheme = (font, textColor) => {
  const fontSize = font?.size || 0;

  const commonStyles = {
    fontFamily: font?.family,
    color: textColor,
  };
  const isCssVar = typeof fontSize !== 'number';

  return {
    h2: {
      fontSize: isCssVar
        ? calcCSSVarSyntax({ cssVar: fontSize, multiply: 1.75, fallback: 28 })
        : `${fontSize * 1.75 || 28}px`,
      ...commonStyles,
    },
    h3: {
      fontSize: isCssVar
        ? calcCSSVarSyntax({ cssVar: fontSize, multiply: 1.375, fallback: 22 })
        : `${fontSize * 1.375 || 22}px`,
      ...commonStyles,
    },
    h4: {
      fontSize: isCssVar
        ? calcCSSVarSyntax({ cssVar: fontSize, multiply: 1, fallback: 16 })
        : `${fontSize * 1 || 16}px`,
      ...commonStyles,
    },
    h5: {
      fontSize: isCssVar
        ? calcCSSVarSyntax({ cssVar: fontSize, multiply: 0.83, fallback: 14 })
        : `${fontSize * 0.83 || 14}px`,
      ...commonStyles,
    },
    h6: {
      fontSize: isCssVar
        ? calcCSSVarSyntax({ cssVar: fontSize, multiply: 0.67, fallback: 11 })
        : `${fontSize * 0.67 || 11}px`,
      ...commonStyles,
    },
    p: {
      fontSize: isCssVar
        ? calcCSSVarSyntax({ cssVar: fontSize, multiply: 1, fallback: 16 })
        : `${fontSize * 1 || 16}px`,
      ...commonStyles,
    },
    quote: {
      fontSize: `${fontSize * 1.125 || 18}px`,
      ...commonStyles,
    },
    link: {
      fontSize: `${fontSize * 1 || 16}px`,
      ...commonStyles,
    },
    hashtag: {
      fontSize: `${font?.size * 1 || 16}px`,
      ...commonStyles,
    },
  };
};

export function getTheme({
  host,
  fontKey,
  extendedFontSize,
  colorKey,
  bgColorKey,
}: {
  host: any;
  fontKey: string;
  extendedFontSize?: number | IChallengeStylesVars;
  colorKey: string;
  bgColorKey: string;
}): RicosTheme {
  if (!host) {
    return;
  }

  const textColor = getColorByKey(host.style, colorKey);
  const actionColor = getParticularColorValue(host.style, 'color_18');
  const bgColor = getColorByKey(host.style, bgColorKey, 'color_11');
  const font = host?.style?.styleParams?.fonts[fontKey];
  const customStyles = {
    ...getCustomTheme(
      extendedFontSize ? { ...font, size: extendedFontSize } : font,
      textColor,
    ),
  };

  return {
    customStyles,
    palette: {
      textColor,
      bgColor,
      actionColor,
    },
  };
}
